import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useAuth } from '../context/AuthContext';
import {
  PLAN_IDS, PLANS, SHOP_PLAN_IDS, SHOP_PLANS, isTestAccount,
} from '../constants/plans';
import { USER_TYPES } from '../constants/designer';
import useCredits from '../hooks/useCredits';
import './TestAccountPanel.css';

// ── TEST ACCOUNT CONTROLS ────────────────────────────────────────────────────
// Sets `plan` and `credits` directly, so the paid tiers and the credit flows can
// be walked through before a payment provider exists.
//
// RENDERS FOR AN ALLOWLIST AND IS ENFORCED FOR AN ALLOWLIST. This component only
// decides whether to DRAW the form; permission lives in firestore.rules
// (isTestAccount), keyed on the verified email inside the Firebase token. Anyone
// who forces this to render — by editing the bundle, or the constant it reads —
// gets a panel whose every write the database refuses. That separation is the
// whole reason it is safe to ship this in a public build.
//
// THE LADDER FOLLOWS THE ACCOUNT'S ROLE, because the two do not overlap: there
// is no bronze shop, and setting `plan: 'bronze'` on one would leave a tier that
// planOfShop cannot resolve, silently falling back to free. The role comes from
// the same `userType` the rest of the app branches on, never from a choice made
// here — this panel grants tiers, it does not change what kind of account this
// is.
//
// Deliberately unstyled as a "feature": it looks like the debug tool it is, so
// nobody mistakes it for part of the product.
//
// ⚠️ DELETE THIS FILE, its import in Plans.jsx, and isTestAccount in
// firestore.rules before taking real money.
export default function TestAccountPanel() {
  const { user, profile } = useAuth();
  const { credits, plan } = useCredits();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  if (!isTestAccount(user)) return null;

  const isShop = profile?.userType === USER_TYPES.SHOP;
  const tiers = isShop ? SHOP_PLANS : PLANS;
  const tierIds = isShop ? SHOP_PLAN_IDS : PLAN_IDS;

  const write = async (patch, label) => {
    setBusy(true);
    setNote('');
    try {
      await updateDoc(doc(db, 'users', user.uid), patch);
      // No local state to update — useCredits is a live snapshot listener, so
      // the header and this panel both follow the write on their own.
      setNote(`${label} ✓`);
    } catch (err) {
      // Almost always the rules refusing it, which is the useful thing to say:
      // it means the deploy has not happened, or the address does not match.
      setNote(`refused: ${err?.code || err?.message || 'unknown'}`);
    } finally {
      setBusy(false);
    }
  };

  const setCredits = () => {
    const n = Math.max(0, Math.min(10000, parseInt(draft, 10) || 0));
    write({ credits: n }, `credits = ${n}`);
  };

  return (
    <section className="tap">
      <h2 className="tap-title">Test account</h2>
      <p className="tap-sub">
        Writes <code>plan</code> and <code>credits</code> straight to your user document.
        Allowed for this address only, by security rule. Not part of the product.
      </p>

      <div className="tap-row">
        <span className="tap-label">Plan</span>
        <div className="tap-btns">
          {tierIds.map(id => (
            <button
              key={id}
              type="button"
              className={`tap-btn${plan === id ? ' tap-btn--on' : ''}`}
              disabled={busy}
              onClick={() => write({ plan: id }, `plan = ${id}`)}
            >{tiers[id].name}</button>
          ))}
        </div>
      </div>

      {/* No credits row for a shop: shops run no AI generations, so they are
          never sold credits and never spend any. A control that sets a balance
          nothing reads would be a way to test a path that does not exist. */}
      {!isShop && (
      <div className="tap-row">
        <span className="tap-label">Credits</span>
        <div className="tap-btns">
          <input
            className="tap-input"
            type="number"
            min="0"
            max="10000"
            placeholder={String(credits ?? 0)}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            disabled={busy}
          />
          <button type="button" className="tap-btn" disabled={busy} onClick={setCredits}>Set</button>
          {/* The two balances worth having on hand: enough for nothing, and
              enough for one staging but not two. */}
          <button type="button" className="tap-btn" disabled={busy} onClick={() => write({ credits: 0 }, 'credits = 0')}>0</button>
          <button type="button" className="tap-btn" disabled={busy} onClick={() => write({ credits: 1 }, 'credits = 1')}>1</button>
          <button type="button" className="tap-btn" disabled={busy} onClick={() => write({ credits: 100 }, 'credits = 100')}>100</button>
        </div>
      </div>
      )}

      {note && <p className="tap-note">{note}</p>}
    </section>
  );
}
